/**
 * The one reader of a schema's document-shaped jsonb columns.
 *
 * Codegen types `schemas.fields` and `schemas.layout` as jsonb
 * (`Record<string, unknown>`), which says nothing about their shape: `fields`
 * is in fact an array. Every reader goes through these parsers instead of
 * asserting the shape. They check the value at run time.
 *
 * A malformed entry THROWS a `SchemaFieldsError`; it is never dropped. Every
 * write goes through `cleanAndValidateFields`, so a bad entry means data that
 * reached the column some other way, and it has to be seen. Dropping it would
 * also lose it for good: a schema editor that re-saves the parsed fields would
 * write the list back without it, and the record values it declared would no
 * longer show.
 */

import type { SchemaField, SchemaLayout } from "../registries/schemas.js"
import { hasFieldType, type SchemaFieldDraft } from "./fields.js"
import { type SavedFilter, savedFilterProblem } from "./filter.js"

/** A schema's `fields` or `layout` jsonb, or a field list given to a schema
 *  write, does not have the shape it must have. */
export class SchemaFieldsError extends Error {
  constructor(where: string, problem: string) {
    super(`${where}: ${problem}`)
    this.name = "SchemaFieldsError"
  }
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

/** Read `entry[prop]` as an optional value of one primitive kind. `undefined`
 *  is absent; `null` is accepted only where the stored shape allows it. */
function optional<T>(
  entry: Record<string, unknown>,
  prop: string,
  is: (v: unknown) => v is T,
  kind: string,
  fail: (problem: string) => never,
  nullable = false
): T | null | undefined {
  const value = entry[prop]
  if (value === undefined) return undefined
  if (value === null && nullable) return null
  if (is(value)) return value
  return fail(
    `"${prop}" must be ${kind}${nullable ? " or null" : ""}, not ${JSON.stringify(value)}`
  )
}

const isString = (v: unknown): v is string => typeof v === "string"
const isBoolean = (v: unknown): v is boolean => typeof v === "boolean"
const isArray = (v: unknown): v is unknown[] => Array.isArray(v)

/** One stored field entry, checked. Properties the type does not name are kept
 *  at run time, as `cleanSchemaFields` keeps them, so a re-save does not lose
 *  them. */
function parseSchemaField(entry: unknown, where: string): SchemaField {
  const fail = (problem: string): never => {
    throw new SchemaFieldsError(where, problem)
  }
  if (!isObject(entry)) return fail(`must be an object, not ${JSON.stringify(entry)}`)

  const { key, type } = entry
  if (typeof key !== "string" || !key) return fail(`"key" must be a non-empty string`)
  if (typeof type !== "string") return fail(`field "${key}": "type" must be a string`)
  if (!hasFieldType(type)) return fail(`field "${key}": unknown type "${type}"`)

  const at = (problem: string): never => fail(`field "${key}": ${problem}`)
  const label = optional(entry, "label", isString, "a string", at)
  const recordType = optional(entry, "recordType", isString, "a string", at, true)
  const subtype = optional(entry, "subtype", isString, "a string", at, true)
  const description = optional(entry, "description", isString, "a string", at, true)
  const isArrayField = optional(entry, "isArray", isBoolean, "a boolean", at)
  const isHidden = optional(entry, "isHidden", isBoolean, "a boolean", at)
  const path = optional(entry, "path", isString, "a string", at)
  const values = optional(entry, "values", isArray, "an array", at)
  const filter = entry.filter
  if (filter !== undefined) {
    const problem = savedFilterProblem(filter)
    if (problem) at(`"filter" ${problem}`)
  }

  return {
    ...entry,
    key,
    type,
    ...(label != null ? { label } : {}),
    ...(recordType !== undefined ? { recordType } : {}),
    ...(subtype !== undefined ? { subtype } : {}),
    ...(description !== undefined ? { description } : {}),
    ...(isArrayField != null ? { isArray: isArrayField } : {}),
    ...(isHidden != null ? { isHidden } : {}),
    ...(path != null ? { path } : {}),
    ...(values != null ? { values } : {}),
    ...(filter !== undefined ? { filter: filter as SavedFilter } : {}),
  }
}

/** A schema's `fields` jsonb as its field list. `where` names the schema in the
 *  error (e.g. `schema #42`). Throws `SchemaFieldsError` on a value that is not
 *  an array, or on any malformed entry. */
export function parseSchemaFields(value: unknown, where: string): SchemaField[] {
  if (!Array.isArray(value)) {
    throw new SchemaFieldsError(where, `fields must be an array, not ${JSON.stringify(value)}`)
  }
  return value.map((entry, i) => parseSchemaField(entry, `${where} fields[${i}]`))
}

/** A field list as an API caller gives it to a schema write, before
 *  `cleanAndValidateFields` cleans and validates it. Only the shape is checked:
 *  the value is an array of objects, and each property `SchemaFieldDraft` names
 *  has that type. Whether an entry is a valid field (key format, known type,
 *  select values) stays the write's check, which reports every problem at
 *  once. Other properties are kept. Throws `SchemaFieldsError`. */
export function parseSchemaFieldDrafts(value: unknown, where: string): SchemaFieldDraft[] {
  if (!Array.isArray(value)) {
    throw new SchemaFieldsError(where, `fields must be an array, not ${JSON.stringify(value)}`)
  }
  return value.map((entry: unknown, i): SchemaFieldDraft => {
    const fail = (problem: string): never => {
      throw new SchemaFieldsError(`${where} fields[${i}]`, problem)
    }
    if (!isObject(entry)) return fail(`must be an object, not ${JSON.stringify(entry)}`)
    const text = (prop: string) => optional(entry, prop, isString, "a string", fail, true)
    const flag = (prop: string) => optional(entry, prop, isBoolean, "a boolean", fail, true)
    return {
      ...entry,
      key: text("key"),
      type: text("type"),
      label: text("label"),
      description: text("description"),
      isHidden: flag("isHidden"),
      isArray: flag("isArray"),
      recordType: text("recordType"),
      values: entry.values,
      path: text("path"),
    }
  })
}

/** A schema's `layout` jsonb: null, or an object whose `version` (when set) is a
 *  number and whose `pages` (when set) is an array. Other keys are kept. */
export function parseSchemaLayout(value: unknown, where: string): SchemaLayout | null {
  if (value == null) return null
  const fail = (problem: string): never => {
    throw new SchemaFieldsError(where, `layout ${problem}`)
  }
  if (!isObject(value)) return fail(`must be an object or null, not ${JSON.stringify(value)}`)
  const isNumber = (v: unknown): v is number => typeof v === "number"
  const version = optional(value, "version", isNumber, "a number", fail)
  const pages = optional(value, "pages", isArray, "an array", fail)
  return {
    ...value,
    ...(version != null ? { version } : {}),
    ...(pages != null ? { pages } : {}),
  }
}

/** A selected `schemas` row with its two jsonb documents parsed. A row that
 *  does not select `layout` gets `layout: null`. */
export function parseSchemaRow<T extends { id: number; fields: unknown; layout?: unknown }>(
  row: T
): Omit<T, "fields" | "layout"> & { fields: SchemaField[]; layout: SchemaLayout | null } {
  const where = `schema #${row.id}`
  return {
    ...row,
    fields: parseSchemaFields(row.fields, where),
    layout: parseSchemaLayout(row.layout, where),
  }
}
